import clsx from "clsx";
import React, { ChangeEvent } from "react";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import TextField from "@material-ui/core/TextField";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";

import { EMPTY_STRING } from "common/constants";
import Loader from "components/Loader";
import NoContent from "components/NoContent";
import SummaryInfo from "components/SummaryInfo";
import { useAppSelector } from "hooks/redux/useAppSelector";

import useStyles from "./styles";

interface IProps {
  horsesInfo: racehorse360.IHorseShortInfo[];
  searchQuery: string;
  isDataLoading: boolean;
  onChangeSearchQuery: (value: string) => void;
  onHorseSelect: (horseInfo: racehorse360.IHorseShortInfo) => void;
}

const HorseSearch = (props: IProps) => {
  const {
    horsesInfo,
    searchQuery,
    isDataLoading,
    onChangeSearchQuery,
    onHorseSelect
  } = props;

  const classes = useStyles();
  const facilities = useAppSelector(state => state.facilities.facilities);

  const shouldShowClearButton = Boolean(searchQuery.length);

  const handleSearchQueryChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChangeSearchQuery(event.currentTarget.value || EMPTY_STRING);
  };

  const handleSearchClose = () => {
    onChangeSearchQuery(EMPTY_STRING);
  };

  const handleHorseSelect = (horseInfo: racehorse360.IHorseShortInfo) => () => {
    onHorseSelect(horseInfo);
  };

  const startAdornment = (
    <InputAdornment position="start">
      <SearchIcon className={classes.searchIcon} />
    </InputAdornment>
  );

  const endAdornment = shouldShowClearButton && (
    <IconButton size="small" onClick={handleSearchClose}>
      <CloseIcon className={classes.clearSearchIcon} />
    </IconButton>
  );

  const renderSearchResults = () => {
    if (isDataLoading) return <Loader />;

    if (!horsesInfo.length) {
      return (
        <NoContent className={classes.noContent}>
          {`No Results for "${searchQuery}"`}
        </NoContent>
      );
    }

    return horsesInfo.map(horse => {
      const isHorseSummaryDisabled = [
        facilities.length === 1,
        facilities[0].id === horse.currentFacilityId
      ].every(Boolean);

      return (
        <SummaryInfo
          key={horse.id}
          classes={{
            root: clsx(classes.horseSummaryRoot, {
              [classes.disabledSummary]: horse.currentFacilityId
            }),
            info: classes.horseSummaryInfo
          }}
          mainTitle={horse.horseName}
          contentNumber={horse.horseRegistrationNumber}
          contentText={horse.trainerName}
          disabled={isHorseSummaryDisabled}
          onItemSelect={handleHorseSelect(horse)}
        />
      );
    });
  };

  return (
    <>
      <TextField
        className={classes.searchInputBase}
        variant="outlined"
        placeholder={"Search Horses"}
        name={"searchHorses"}
        value={searchQuery}
        onChange={handleSearchQueryChange}
        autoComplete={"off"}
        InputProps={{
          classes: {
            input: classes.searchInput
          },
          startAdornment: startAdornment,
          endAdornment: endAdornment
        }}
      />
      {searchQuery && (
        <div className={classes.searchResults}>{renderSearchResults()}</div>
      )}
    </>
  );
};

export default React.memo(HorseSearch);
